import React from 'react';
import { Box, Text, Avatar } from '@mantine/core';

interface ResponsivePlayerStackProps {
  avatar?: string | null;
  name: string;
  onClick?: () => void;
  scale?: number; // Scale factor (1 = default size, 2 = double size)
  handwritingFont?: string; // Custom handwriting font for the name
}

const ResponsivePlayerStack: React.FC<ResponsivePlayerStackProps> = ({ 
  avatar, 
  name, 
  onClick,
  scale = 1,
  handwritingFont
}) => {
  // Base dimensions
  const baseWidth = 120;
  const baseHeight = 140;
  const basePadding = 8;
  const basePhotoSize = 104;
  const baseBorderRadius = 5;
  const baseNameHeight = 20;
  
  // Scaled dimensions
  const width = baseWidth * scale;
  const height = baseHeight * scale;
  const padding = basePadding * scale;
  const photoSize = basePhotoSize * scale;
  const borderRadius = baseBorderRadius * scale;
  const nameHeight = baseNameHeight * scale;
  const fontSize = 12 * scale; // xs = 12px base
  const avatarFallbackSize = 80 * scale;
  const avatarFontSize = 32 * scale;
  
  return (
    <Box
      style={{
        width: `${width}px`,
        height: `${height}px`,
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {/* Single card - polaroid style */}
      <Box
        style={{
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: 'white',
          borderRadius: `${borderRadius}px`,
          padding: `${padding}px`,
          boxShadow: '0 4px 8px rgba(13, 163, 136, 0.15)',
        }}
      >
        {/* Avatar photo area - full bleed */}
        <Box
          style={{
            width: `${photoSize}px`,
            height: `${photoSize}px`,
            backgroundColor: '#ffffff',
            borderRadius: `${borderRadius / 2}px`,
            marginBottom: `${padding}px`,
            overflow: 'hidden',
            border: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {avatar ? (
            <img
              src={avatar}
              alt={`${name}'s avatar`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#ffffff',
              }}
            />
          ) : (
            <Avatar
              size={avatarFallbackSize}
              radius="sm"
              color="blue"
              style={{
                fontSize: `${avatarFontSize}px`,
                fontWeight: 'bold',
              }}
            >
              {name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
          )}
        </Box>

        {/* Name text area */}
        <Text
          size="xs"
          fw={500}
          ta="center"
          style={{
            fontFamily: handwritingFont ? `"${handwritingFont}", var(--mantine-font-family)` : 'var(--mantine-font-family)',
            color: '#666',
            lineHeight: 1.2,
            height: `${nameHeight}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: `${fontSize}px`,
          }}
        >
          {name}
        </Text>
      </Box>
    </Box>
  );
};

export default ResponsivePlayerStack;
